import { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { VALID_ROLES } from '../lib/roleLabels';
import { PENDING_KEY, confirmRedirect } from '../lib/signupCompletion';

const UserContext = createContext();

const EMPTY_USER = {
  name: '',
  role: '',
  agency: '',
  interests: [],
  verified: false,
  avatar_url: null,
  bio: null,
  app_role: null,
  email: null,
  zip_code: null,
  location_consent_at: null,
  verification_status: 'none',
  verification_rejection_reason: null,
  years_experience: null,
  show_agency: true,
  show_bio: true,
  show_interests: true,
  username: null,
  use_username: false,
  terms_accepted_at: null,
  terms_version: null,
};

export const UserProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [userData, setUserData] = useState(EMPTY_USER);
  const [loading, setLoading] = useState(true);
  const [recoveryMode, setRecoveryMode] = useState(false);

  const fetchProfile = async (userId, authEmail) => {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();
    if (!error && data) {
      setUserData({
        name: data.name || '',
        role: data.role || '',
        agency: data.agency || '',
        interests: data.interests || [],
        verified: data.verified || false,
        avatar_url: data.avatar_url || null,
        bio: data.bio || null,
        app_role: data.app_role || 'member',
        email: data.email || authEmail || null,
        zip_code: data.zip_code || null,
        location_consent_at: data.location_consent_at ?? null,
        verification_status: data.verification_status || 'none',
        verification_rejection_reason: data.verification_rejection_reason || null,
        years_experience: data.years_experience ?? null,
        show_agency: data.show_agency ?? true,
        show_bio: data.show_bio ?? true,
        show_interests: data.show_interests ?? true,
        username: data.username || null,
        use_username: data.use_username ?? false,
        terms_accepted_at: data.terms_accepted_at || null,
        terms_version: data.terms_version || null,
      });
      // Backfill email for users created before this column existed (best-effort, fire-and-forget)
      if (!data.email && authEmail) {
        supabase.from('profiles').upsert({ id: userId, email: authEmail });
      }
    }
  };

  const refreshProfile = async () => {
    const { data: { session: currentSession } } = await supabase.auth.getSession();
    if (currentSession) {
      await fetchProfile(currentSession.user.id, currentSession.user.email);
    }
  };

  useEffect(() => {
    supabase.auth.getSession()
      .then(({ data: { session } }) => {
        setSession(session);
        if (session) {
          fetchProfile(session.user.id, session.user.email).finally(() => setLoading(false));
        } else if (window.location.hash.includes('access_token=')) {
          // Hash-based auth in progress (magic link / recovery) — onAuthStateChange fires shortly
        } else {
          setLoading(false);
        }
      })
      .catch(() => setLoading(false));

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') setRecoveryMode(true);
      setSession(session);
      if (session) {
        fetchProfile(session.user.id, session.user.email).finally(() => setLoading(false));
      } else {
        setUserData(EMPTY_USER);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // pendingSignup: what needs a session to apply (terms record, years, zip,
  // a ?code=) — see src/lib/signupCompletion.js. With email confirmation on
  // there is no session until the member confirms.
  const signUp = async (email, password, profileData = {}, pendingSignup = null) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: confirmRedirect(),
        data: {
          name:         profileData.name         || null,
          role:         profileData.role         || null,
          agency:       profileData.agency       || null,
          interests:    profileData.interests    || [],
          username:     profileData.username     || null,
          use_username: profileData.use_username ?? false,
          ...(pendingSignup ? { [PENDING_KEY]: pendingSignup } : {}),
        },
      },
    });
    return { data, error };
  };

  const signIn = async (email, password) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error };
  };

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
    } catch {
      // Sign-out failed (network error) — clear local state anyway so UI recovers
      setSession(null);
      setUserData(EMPTY_USER);
    }
  };

  const deleteAccount = async () => {
    const { data: { session: currentSession } } = await supabase.auth.getSession();
    if (!currentSession) return { error: new Error('Not authenticated') };
    const { error } = await supabase.functions.invoke('delete-account', {
      headers: { Authorization: `Bearer ${currentSession.access_token}` },
    });
    if (!error) {
      setSession(null);
      setUserData(EMPTY_USER);
    }
    return { error };
  };

  // zip_code is deliberately absent: it is saved only through set_my_zip_code
  // (054), which records the opt-in. A direct write here would store it uncounted.
  const ALLOWED_PROFILE_FIELDS = ['name', 'role', 'agency', 'bio', 'avatar_url', 'interests', 'years_experience', 'show_agency', 'show_bio', 'show_interests', 'use_username', 'terms_accepted_at', 'terms_version'];

  const updateProfile = async (fields, overrideUserId) => {
    let userId = overrideUserId || session?.user?.id;
    if (!userId) {
      const { data } = await supabase.auth.getSession();
      userId = data?.session?.user?.id;
    }
    if (!userId) return { error: new Error('Not authenticated') };
    const filtered = Object.fromEntries(
      Object.entries(fields).filter(([k]) => ALLOWED_PROFILE_FIELDS.includes(k))
    );
    const sanitized = { ...filtered };
    if (typeof sanitized.name === 'string')    sanitized.name    = sanitized.name.trim().slice(0, 100);
    if (typeof sanitized.agency === 'string')  sanitized.agency  = sanitized.agency.trim().slice(0, 150);
    if (typeof sanitized.bio === 'string')     sanitized.bio     = sanitized.bio.trim().slice(0, 500);
    if ('role' in sanitized && !VALID_ROLES.includes(sanitized.role)) delete sanitized.role;
    const { error } = await supabase
      .from('profiles')
      .upsert({ id: userId, ...sanitized });
    if (error) {
      console.error('[updateProfile] upsert failed:', error.code, error.message, error.details, { sanitized });
    } else {
      setUserData(prev => ({ ...prev, ...sanitized }));
    }
    return { error };
  };

  const verified = userData.verified;

  return (
    <UserContext.Provider value={{
      session,
      userData,
      setUserData,
      verified,
      loading,
      recoveryMode,
      signUp,
      signIn,
      signOut,
      deleteAccount,
      updateProfile,
      refreshProfile,
    }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) throw new Error('useUser must be used within a UserProvider');
  return context;
};
